import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";

import { App } from "./app/App";
import { RootErrorBoundary } from "@/app/common/components/composite/shell/RootErrorBoundary";
import {
  applyColorScheme,
  preferredColorScheme,
} from "@generalapplications/ui";
import { themeColors, themeStorageKey } from "@/app/common/styles/theme";
// The site's own Tailwind build first, the library's finished stylesheet
// after it: both write the same cascade layers, so whichever loads second
// wins a tie, and the library's responsive utilities (a `lg:grid` beside a
// `flex`) must outrank the site's static ones for its layouts to hold.
import "@/app/common/styles/globals.css";

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found.");
}

applyColorScheme(preferredColorScheme(themeStorageKey), themeColors);

// RootErrorBoundary renders no DOM of its own when there's no error (just
// passes children through), so wrapping it here only — not in
// entry-server.tsx — introduces no server/client markup mismatch.
const app = (
  <StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </StrictMode>
);

// Production routes ship prerendered markup inside #root (see entry-server.tsx
// and scripts/prerender.mjs), so hydrate over it instead of a full client
// render. The dev server serves the unbuilt index.html, whose #root still
// holds the raw `<!-- prerender-root -->` placeholder comment — a comment
// node still counts for hasChildNodes(), so check for an actual element
// child instead, or dev would call hydrateRoot on nothing and hard-fault
// with a hydration mismatch on every load.
if (rootElement.firstElementChild) {
  hydrateRoot(rootElement, app);
} else {
  createRoot(rootElement).render(app);
}
